import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const MAX_RESULTS = 60;

/**
 * Searchable project picker.
 *
 * Filters across project_number, name, client_name, project_type and
 * location. Each result line shows:
 *   [LIC-2026-001]  Project Name
 *   Cliente XYZ · Tipo · Localização
 *
 * Designed as a drop-in replacement for the previous fixed `<Select>` in
 * the financial entry form so the user can find the right project by
 * typing any fragment that helps disambiguate.
 */
export default function ProjectCombobox({
  projects,
  value,
  onChange,
  placeholder = 'Selecione o projeto',
  allowEmpty = false,
  emptyLabel = 'Sem projeto vinculado',
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = useMemo(
    () => projects.find(p => p.project_id === value),
    [projects, value],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects.slice(0, MAX_RESULTS);
    const out = [];
    for (const p of projects) {
      if (out.length >= MAX_RESULTS) break;
      const haystack = [
        p.project_number, p.name, p.client_name, p.project_type, p.location,
      ].filter(Boolean).join(' ').toLowerCase();
      if (haystack.includes(q)) out.push(p);
    }
    return out;
  }, [projects, search]);

  const renderTrigger = () => {
    if (selected) {
      return (
        <span className="flex items-center gap-2 truncate text-left">
          {selected.project_number && (
            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
              {selected.project_number}
            </span>
          )}
          <span className="truncate">{selected.name}</span>
        </span>
      );
    }
    if (allowEmpty && (value === '' || value == null)) {
      return <span className="text-gray-500">{emptyLabel}</span>;
    }
    return <span className="text-gray-500">{placeholder}</span>;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal"
          data-testid={testId || 'project-combobox-trigger'}
        >
          {renderTrigger()}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Código, nome, cliente, tipo ou local..."
            value={search}
            onValueChange={setSearch}
            data-testid="project-combobox-input"
          />
          <CommandList className="max-h-72">
            <CommandEmpty>Nenhum projeto encontrado</CommandEmpty>
            <CommandGroup>
              {allowEmpty && (
                <CommandItem
                  value="__none"
                  onSelect={() => {
                    onChange('');
                    setOpen(false);
                    setSearch('');
                  }}
                  data-testid="project-combobox-none"
                >
                  <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                  <span className="italic text-gray-500">{emptyLabel}</span>
                </CommandItem>
              )}
              {filtered.map(p => (
                <CommandItem
                  key={p.project_id}
                  value={p.project_id}
                  onSelect={() => {
                    onChange(p.project_id);
                    setOpen(false);
                    setSearch('');
                  }}
                  data-testid={`project-option-${p.project_id}`}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === p.project_id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {p.project_number && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 shrink-0">
                          {p.project_number}
                        </span>
                      )}
                      <span className="truncate font-medium">{p.name}</span>
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {[p.client_name, p.project_type, p.location].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {filtered.length === MAX_RESULTS && (
              <div className="px-3 py-1.5 text-[11px] text-gray-400 italic">
                Mostrando os primeiros {MAX_RESULTS} — refine a busca para ver mais
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
